import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const DEMO_EMAIL = 'demo@propertymgmt.com';
const TOUR_STORAGE_KEY = 'demoTourCompleted_v1';

const TOUR_STEPS = [
  { path: '/app/dashboard', titleKey: 'demoTour.steps.dashboard.title', bodyKey: 'demoTour.steps.dashboard.body' },
  { path: '/app/properties', titleKey: 'demoTour.steps.properties.title', bodyKey: 'demoTour.steps.properties.body' },
  { path: '/app/tenants', titleKey: 'demoTour.steps.tenants.title', bodyKey: 'demoTour.steps.tenants.body' },
  { path: '/app/finances', titleKey: 'demoTour.steps.balance.title', bodyKey: 'demoTour.steps.balance.body' },
  { path: '/app/financial', titleKey: 'demoTour.steps.financial.title', bodyKey: 'demoTour.steps.financial.body' },
  { path: '/app/accounting/reports', titleKey: 'demoTour.steps.reports.title', bodyKey: 'demoTour.steps.reports.body' },
  { path: '/app/maintenance', titleKey: 'demoTour.steps.maintenance.title', bodyKey: 'demoTour.steps.maintenance.body' },
  { path: '/app/meetings', titleKey: 'demoTour.steps.meetings.title', bodyKey: 'demoTour.steps.meetings.body' },
  { path: '/app/documents', titleKey: 'demoTour.steps.documents.title', bodyKey: 'demoTour.steps.documents.body' },
  { path: '/app/ai-insights', titleKey: 'demoTour.steps.ai.title', bodyKey: 'demoTour.steps.ai.body' },
  { path: '/app/settings', titleKey: 'demoTour.steps.settings.title', bodyKey: 'demoTour.steps.settings.body' },
  { path: '/app/pricing', titleKey: 'demoTour.steps.pricing.title', bodyKey: 'demoTour.steps.pricing.body' },
];

export default function DemoTourGuide() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isDemo = (user?.email || '').toLowerCase() === DEMO_EMAIL;
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!isDemo) return;
    const completed = localStorage.getItem(TOUR_STORAGE_KEY) === '1';
    if (!completed) setOpen(true);
  }, [isDemo]);

  const step = TOUR_STEPS[stepIndex];
  const progress = useMemo(
    () => Math.round(((stepIndex + 1) / TOUR_STEPS.length) * 100),
    [stepIndex],
  );

  if (!isDemo || !open) return null;

  const finish = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
    setOpen(false);
  };

  const next = () => {
    if (stepIndex >= TOUR_STEPS.length - 1) {
      finish();
      return;
    }
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    navigate(TOUR_STEPS[nextIndex].path);
  };

  const prev = () => {
    if (stepIndex <= 0) return;
    const nextIndex = stepIndex - 1;
    setStepIndex(nextIndex);
    navigate(TOUR_STEPS[nextIndex].path);
  };

  const goToCurrentStep = () => {
    if (location.pathname !== step.path) navigate(step.path);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[22rem] max-w-[calc(100vw-2rem)]" data-testid="demo-tour-guide">
      <Card className="border-domio-200 shadow-xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-manrope">{t('demoTour.title')}</CardTitle>
          <p className="text-xs text-slate-500">
            {t('demoTour.stepCounter', { current: stepIndex + 1, total: TOUR_STEPS.length })}
          </p>
          <Progress value={progress} />
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <p className="font-semibold text-slate-900">{t(step.titleKey)}</p>
            <p className="text-sm text-slate-600">{t(step.bodyKey)}</p>
          </div>
          {location.pathname !== step.path && (
            <Button variant="outline" size="sm" onClick={goToCurrentStep} className="w-full">
              {t('demoTour.goToStep')}
            </Button>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={finish}>
              {t('demoTour.skip')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={prev} disabled={stepIndex === 0}>
                {t('demoTour.prev')}
              </Button>
              <Button size="sm" className="bg-domio-600 hover:bg-domio-700" onClick={next}>
                {stepIndex === TOUR_STEPS.length - 1 ? t('demoTour.finish') : t('demoTour.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
